import React, { useMemo, useState } from 'react';
import PlacementsTable from '../../components/PlacementsTable';
import { TEAM_LABELS } from '../../config';
import { rangeOf } from '../../dates';
import { addDays, fmtDate, fmtMoney, isoDate, useFetch } from '../../hooks';
import { Alert, Card, Input, Segmented, Select, Skeleton } from '../../ui/basics';
import { BarChart } from '../../ui/charts';

const PERIODS = ['Last Week', 'Current Week', 'Last Month', 'Current Month', 'Last Year', 'Current Year', 'Custom'];

export default function Period({ org, lead }) {
  const [period, setPeriod] = useState('Last Week');
  const [custom, setCustom] = useState({ from: isoDate(addDays(new Date(), -30)), to: isoDate(new Date()) });
  const [metric, setMetric] = useState('count');
  const [pick, setPick] = useState({ kind: 'started', from: '', to: '', lead: '' });
  const [start, end] = period === 'Custom' ? [custom.from, custom.to] : rangeOf(period).map(isoDate);
  const valid = start && end && start <= end;
  const { data, loading, error } = useFetch(valid ? 'reports/period/' : null, { organization: org, lead, from_date: start, to_date: end });

  const chart = useMemo(() => {
    if (!data) return { rows: [], monthly: false };
    const monthly = data.daily.length > 62;
    const buckets = new Map();
    data.daily.forEach((d) => {
      const key = monthly ? d.date.slice(0, 7) : d.date;
      const b = buckets.get(key) || { key, Started: 0, Ended: 0 };
      b.Started += metric === 'count' ? d.started : d.started_margin;
      b.Ended += metric === 'count' ? d.ended : d.ended_margin;
      buckets.set(key, b);
    });
    const rows = [...buckets.values()].map((b) => ({ ...b, label: monthly ? b.key : fmtDate(b.key).slice(0, 5) }));
    return { rows, monthly };
  }, [data, metric]);

  const select = (kind, from, to, team = '') => setPick({ kind, from, to, lead: team });
  const onBar = (bar) => {
    const key = bar.data.key;
    const from = chart.monthly ? `${key}-01` : key;
    const to = chart.monthly ? isoDate(new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)), 0)) : key;
    select(bar.id.toLowerCase(), from < start ? start : from, to > end ? end : to);
  };
  const active = pick.from ? pick : { kind: pick.kind, from: start, to: end, lead: '' };
  const filters = { ...(active.kind === 'started' ? { joined_from: active.from, joined_to: active.to } : { ended_from: active.from, ended_to: active.to }),
    ...(active.lead ? { lead: active.lead } : {}) };

  return (
    <div className="stack">
      <Card title="Team activity" subtitle={valid ? `${fmtDate(start)} – ${fmtDate(end)}` : 'Choose a valid date range'}
        actions={<div className="row">
          <Select value={period} onChange={(e) => { setPeriod(e.target.value); setPick({ kind: 'started', from: '', to: '', lead: '' }); }} options={PERIODS} aria-label="Period" />
          {period === 'Custom' && <>
            <Input type="date" style={{ width: 150 }} invalid={!valid} value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} aria-label="From date" />
            <Input type="date" style={{ width: 150 }} invalid={!valid} value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} aria-label="To date" />
          </>}
        </div>}>
        {error && <Alert>{error}</Alert>}
        {!valid ? <Alert tone="warning">{start && end ? 'The start date must be on or before the end date.' : 'Choose both a start and an end date.'}</Alert> : loading ? <Skeleton h={120} /> : data && (
          <div className="grid cols-3">
            {Object.entries(data.teams).map(([team, t]) => (
              <div key={team} className="card" style={{ padding: 16 }}>
                <h3>{TEAM_LABELS[team]}</h3>
                <div className="grid cols-2" style={{ marginTop: 10 }}>
                  <button type="button" className="link" style={{ textAlign: 'left' }} onClick={() => select('started', start, end, team)}>
                    <span className="muted" style={{ fontWeight: 500 }}>Started</span><div className="num" style={{ fontSize: 22 }}>{t.started}</div>
                    <span className="muted" style={{ fontWeight: 500 }}>{fmtMoney(t.started_margin)}</span></button>
                  <button type="button" className="link" style={{ textAlign: 'left' }} onClick={() => select('ended', start, end, team)}>
                    <span className="muted" style={{ fontWeight: 500 }}>Ended</span><div className="num" style={{ fontSize: 22 }}>{t.ended}</div>
                    <span className="muted" style={{ fontWeight: 500 }}>{fmtMoney(t.ended_margin)}</span></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {valid && data && (
        <Card title={`${metric === 'count' ? 'Candidates' : 'Margin'} started and ended ${chart.monthly ? 'per month' : 'per day'}`} subtitle="Click a bar to list those candidates"
          actions={<Segmented value={metric} onChange={setMetric} options={[{ value: 'count', label: 'Candidates' }, { value: 'margin', label: 'Margin' }]} />}>
          <BarChart data={chart.rows} keys={['Started', 'Ended']} indexBy="label" onClick={onBar} rotate={chart.monthly ? 0 : -45}
            colors={['#12b76a', '#f04438']} valueFormat={metric === 'margin' ? (v) => fmtMoney(v) : undefined} />
        </Card>
      )}

      {valid && (
        <Card flush title={`Candidates ${active.kind} ${active.from === active.to ? `on ${fmtDate(active.from)}` : `between ${fmtDate(active.from)} and ${fmtDate(active.to)}`}${active.lead ? ` · ${TEAM_LABELS[active.lead]}` : ''}`}
          actions={<Segmented value={active.kind} onChange={(k) => setPick({ ...active, kind: k })} options={[{ value: 'started', label: 'Started' }, { value: 'ended', label: 'Ended' }]} />}>
          <PlacementsTable key={`${active.kind}-${active.from}-${active.to}-${active.lead}`} fixed={{ organization: org, lead }} initialFilters={filters} initialSize={10} />
        </Card>
      )}
    </div>
  );
}
